import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Box,
    Alert,
    Divider,
    Tooltip,
    IconButton,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import axiosClient from '../../../../../lib/axios';
import { defValueImposto } from '../../../../../components/BasicComponents/AdicionarImpostos/DialogContentAdicionarImposto';
import { formatarInputValorMonetárioSemPrefixo, formatarMonetárioParaNúmero, formatarTelefone, permitirApenasNumeros, porcentagemParaNumero } from '../../../../../helpers/masks';
import AdicionarImpostos, { TAdicionarImposto } from '../../../../../components/BasicComponents/AdicionarImpostos';
import { ICotacaoFornecedor } from '../../../../../types/api/estoque/obter-cotacao-fornecedor';
import tratativaDeDatas from '../../../../../helpers/tratativaDeDatas';
import SkeletonFormCotação from './SkeletonFormCotacao';
import { TCotacaoFornecedorMutation } from '../../../../../types/mutations/estoque/atualizar-cotacao';
import ConfirmacaoEnvio from './ConfirmacaoEnvio';
import logoTechPines from '../../../../../assets/techpines-background-white.png';
import { desformatarTelefone } from '../../../../../helpers/unmask';
import moment, { Moment } from 'moment';
import BasicDatePicker from '../../../../../components/BasicDatePicker';
import DialogConfirmarEnvioFornecedor from './DialogConfirmarEnvioFornecedor';


export default function FormCotacaoFornecedor() {
    const navigate = useNavigate();
    const { cotacaoId } = useParams<{ cotacaoId: string }>();

    const [cotacaoEnviada, setCotacaoEnviada] = useState(false);
    const [openConfirmacao, setOpenConfirmacao] = useState(false);

    const { data: cotacaoData, isLoading, error } = useQuery<ICotacaoFornecedor>({
        queryKey: ['cotacao-fornecedor', cotacaoId],
        queryFn: async () => {
            const response = await axiosClient.get(`/estoque/compras/obter-cotacao-fornecedor/${cotacaoId}`);
            return response.data;
        },
        onError: () => {
            enqueueSnackbar('Link de cotação inválido ou expirado', { variant: 'error' });
            setTimeout(() => navigate('/'), 3000);
        }
    });

    const { mutate: enviarCotacao, isLoading: enviando } = useMutation({
        mutationFn: (inputs: TCotacaoFornecedorMutation) =>
            axiosClient.post(`/estoque/compras/atualizar-cotacao/${cotacaoId}`, inputs),
        onSuccess: () => {
            enqueueSnackbar('Cotação enviada com sucesso!', { variant: 'success' });
            setCotacaoEnviada(true);
        },
        onError: () => {
            enqueueSnackbar('Erro ao enviar cotação', { variant: 'error' });
        }
    });

    const methods = useForm<TCotacaoFornecedorInputs>({
        defaultValues: {
            tipo_frete: "CIF",
            valor_frete: "0,00",
            prazo: "",
            produtos: []
        }
    });

    const { control, handleSubmit, setValue, getValues, watch } = methods;

    const onSubmit = (data: TCotacaoFornecedorInputs) => {
        const params: TCotacaoFornecedorMutation = {
            produtos: data.produtos.map(produto => ({
                produto_id: produto.produto_id,
                quantidade: produto.quantidade,
                valor_unitario: formatarMonetárioParaNúmero(produto.valor_unitario),
                valor_total: formatarMonetárioParaNúmero(produto.valor_total),
                impostos: produto.impostos.filter(imposto => imposto.aplica).map(imposto => ({
                    tipo: imposto.nome,
                    valor: porcentagemParaNumero(imposto.valor),
                    incluso_preco: imposto.incluso_preco,
                    direito_credito: imposto.direito_credito
                })),
                codigo_produto_fornecedor: produto.codigo_produto_fornecedor || null,
            })),
            tipo_frete: data.tipo_frete,
            valor_frete: formatarMonetárioParaNúmero(data.valor_frete),
            prazo: Number(data.prazo),
            vencimento_cotacao: data.vencimento_cotacao ? data.vencimento_cotacao.format("YYYY-MM-DD") : '',
            nome_vendedor: data.nome_vendedor || null,
            telefone_vendedor: data.telefone_vendedor ? desformatarTelefone(data.telefone_vendedor) : null,
            observacoes: data.observacoes || null
        };
        setOpenConfirmacao(false);
        enviarCotacao(params);
    };

    useEffect(() => {
        if (cotacaoData?.cotacao?.itens) {
            setValue('produtos', cotacaoData.cotacao.itens.map(item => ({
                id: item.id,
                produto_id: item.produto_id,
                nome: item.produto_nome,
                quantidade: item.quantidade,
                unidade_medida: item.unidade_medida,
                observacoes: item.observacoes_fornecedor || "",
                valor_unitario: "0,00",
                valor_total: "0,00",
                impostos: defValueImposto
            })));
        }
        if (cotacaoData?.fornecedor?.nome) {
            setValue('nome_vendedor', cotacaoData.fornecedor.nome);
        }
        if (cotacaoData?.cotacao?.respondido_em) {
            setCotacaoEnviada(true);
        }
    }, [cotacaoData, setValue]);

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <SkeletonFormCotação />
            </Container>
        );
    }

    if (error || !cotacaoData || Object.keys(cotacaoData).length === 0) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 6, textAlign: 'center' }}>
                    <Alert
                        severity="warning"
                        sx={{
                            fontSize: '1.1rem',
                            '& .MuiAlert-message': {
                                width: '100%'
                            }
                        }}
                    >
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                                Link de cotação inválido ou expirado
                            </Typography>

                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                Entre em contato com a cooperativa que emitiu a solicitação de cotação para obter um novo link válido.
                            </Typography>
                        </Box>
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (cotacaoEnviada) {
        return (
            <ConfirmacaoEnvio nomeFornecedor={cotacaoData?.fornecedor?.nome || ''} dataCotacao={cotacaoData?.cotacao?.respondido_em || moment().format('DD/MM/YYYY HH:mm')} />
        );
    }

    const produtos = watch("produtos");

    return (
        <FormProvider {...methods}>
            <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.100', py: 4 }}>
                <Container maxWidth="lg">
                    <Paper elevation={3} sx={{ p: 4 }}>
                        <Box textAlign="center" sx={{ mb: 3 }}>
                            <img
                                src="/imgs/config-img.png"
                                alt="Coopervas"
                                style={{ maxHeight: '80px', marginBottom: '16px' }}
                            />
                        </Box>
                        <Box textAlign="center" sx={{ mb: 4 }}>
                            <Typography variant="h4" component="h1" color="primary" gutterBottom>
                                Cotação de Fornecedor
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                {cotacaoData?.fornecedor?.nome}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Controller
                                        name="nome_vendedor"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                fullWidth
                                                label="Nome do vendedor"
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Controller
                                        name="telefone_vendedor"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                fullWidth
                                                label="Telefone"
                                                value={field.value}
                                                onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Controller
                                        name="vencimento_cotacao"
                                        control={control}
                                        rules={{
                                            required: "Vencimento da cotação é obrigatório",
                                            validate: (value) => {
                                                if (value && value.isBefore(moment(), 'day')) {
                                                    return "A data de vencimento não pode ser anterior a hoje";
                                                }
                                            }
                                        }}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <BasicDatePicker
                                                required
                                                label="Vencimento da cotação"
                                                blockPastDates
                                                value={field.value}
                                                onChange={(data) => field.onChange(data)}
                                                formatString="DD/MM/YYYY"
                                                error={invalid}
                                                helperText={error?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Detalhes da Solicitação
                            </Typography>
                            <Grid container spacing={3}>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="text.secondary">Criada em</Typography>
                                    <Typography variant="body1">
                                        {
                                            cotacaoData?.cotacao?.criado_em &&
                                            tratativaDeDatas(cotacaoData.cotacao.criado_em, false, 'DD/MM/YYYY HH:mm')
                                        }
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="text.secondary">Vencimento</Typography>
                                    <Typography variant="body1">
                                        {
                                            cotacaoData?.cotacao?.vencimento &&
                                            tratativaDeDatas(cotacaoData.cotacao.vencimento, false, 'DD/MM/YYYY HH:mm')
                                        }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ mb: 4 }} />
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom color="primary" mb={2}>
                                Configurações da Cotação
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Controller
                                        name="tipo_frete"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth>
                                                <InputLabel>Tipo de Frete</InputLabel>
                                                <Select
                                                    value={field.value}
                                                    label="Tipo de Frete"
                                                    onChange={field.onChange}
                                                >
                                                    <MenuItem value="CIF">CIF</MenuItem>
                                                    <MenuItem value="FOB">FOB</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Controller
                                        name="valor_frete"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                fullWidth
                                                label="Valor do Frete"
                                                value={field.value}
                                                onChange={(e) => field.onChange(formatarInputValorMonetárioSemPrefixo(e.target.value))}
                                                slotProps={{
                                                    input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Controller
                                        name="prazo"
                                        control={control}
                                        rules={{
                                            required: {
                                                value: true,
                                                message: 'Prazo de entrega é obrigatório'
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Prazo de Entrega (dias)"
                                                value={field.value}
                                                onChange={(e) => field.onChange(permitirApenasNumeros(e.target.value))}
                                                slotProps={{ htmlInput: { min: 0 } }}
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Produtos e Valores
                            </Typography>

                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Produto</b></TableCell>
                                        <TableCell><b>Quantidade</b></TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <b>Código do Produto</b>
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ p: 0.5 }}>
                                                            <Typography variant="body2" fontWeight="bold" gutterBottom>
                                                                Código do Produto / EAN
                                                            </Typography>
                                                            <Typography variant="body2" paragraph>
                                                                Este campo é destinado para você informar o código interno que sua empresa utiliza para identificar este produto em seu sistema.
                                                            </Typography>
                                                            <Typography variant="body2" paragraph>
                                                                Pode ser:
                                                            </Typography>
                                                            <Typography variant="body2" component="div">
                                                                • Código EAN/GTIN (código de barras)
                                                            </Typography>
                                                            <Typography variant="body2" component="div">
                                                                • Código interno do fornecedor
                                                            </Typography>
                                                            <Typography variant="body2" component="div">
                                                                • Referência do fabricante
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                                Este código ajuda na identificação correta do produto durante o processo de compra e recebimento.
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    arrow
                                                    placement="top"
                                                >
                                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                                        <HelpOutlineIcon fontSize="small" color="action" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell><b>Valor Unitário</b></TableCell>
                                        <TableCell><b>Valor Total</b></TableCell>
                                        <TableCell><b>Impostos</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {produtos.map((produto, index) => (
                                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {produto.produto_id}
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {produto.nome}
                                                    </Typography>
                                                    {produto.observacoes && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            {produto.observacoes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body1">
                                                    {produto.quantidade} {produto.unidade_medida}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`produtos.${index}.codigo_produto_fornecedor`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            size="small"
                                                            label="Código do Produto/EAN"
                                                            placeholder="Código do produto/EAN"
                                                            value={field.value || ''}
                                                            onChange={(e) => field.onChange(e.target.value)}
                                                            sx={{ minWidth: 150 }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`produtos.${index}.valor_unitario`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            size="small"
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                const valorUnitarioCentavos = formatarMonetarioParaCentavos(e.target.value);
                                                                const quantidade = Number(getValues(`produtos.${index}.quantidade`)) || 0;
                                                                const valorTotalCentavos = valorUnitarioCentavos * quantidade;
                                                                const valorUnitarioFormatado = formatarCentavosParaMonetario(valorUnitarioCentavos);
                                                                const valorTotalFormatado = formatarCentavosParaMonetario(valorTotalCentavos);
                                                                setValue(`produtos.${index}.valor_total`, valorTotalFormatado);
                                                                field.onChange(valorUnitarioFormatado);
                                                            }}
                                                            slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`produtos.${index}.valor_total`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            size="small"
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                const novoValor = formatarInputValorMonetárioSemPrefixo(e.target.value);
                                                                field.onChange(novoValor);
                                                            }}
                                                            slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } }}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`produtos.${index}.impostos`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <AdicionarImpostos
                                                            inputSize="small"
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Divider sx={{ mb: 4 }} />

                        <Box sx={{ mb: 4 }}>
                            <Controller
                                name={`observacoes`}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        size="small"
                                        value={field.value}
                                        multiline
                                        rows={4}
                                        label="Observações"
                                        fullWidth
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                disabled={enviando}
                                sx={{ minWidth: 200 }}
                                onClick={() => {
                                    handleSubmit(
                                        () => {
                                            setOpenConfirmacao(true);
                                        },
                                        (errors) => {
                                            enqueueSnackbar('Por favor, corrija os erros no formulário antes de enviar.', { variant: 'warning' });
                                        }
                                    )();
                                }}
                            >
                                {enviando ? 'Enviando...' : 'Enviar Cotação'}
                            </Button>
                        </Box>
                        <Divider sx={{ mt: 4, mb: 2 }} />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                opacity: 0.7
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Powered by
                            </Typography>
                            <img
                                src={logoTechPines}
                                alt="TechPines"
                                style={{ height: '50px' }}
                            />
                        </Box>
                    </Paper>
                </Container>
            </Box>
            {
                openConfirmacao &&
                <DialogConfirmarEnvioFornecedor
                    open={openConfirmacao}
                    onClose={() => setOpenConfirmacao(false)}
                    onConfirm={handleSubmit(onSubmit)}
                />
            }
        </FormProvider>
    );
}

export type TCotacaoFornecedorInputs = {
    observacoes: string;
    nome_vendedor?: string;
    telefone_vendedor?: string;
    tipo_frete: "CIF" | "FOB";
    valor_frete: string;
    prazo: string;
    produtos: Array<{
        id: number;
        produto_id: number;
        nome: string;
        quantidade: number;
        unidade_medida: string;
        observacoes: string;
        valor_unitario: string;
        valor_total: string;
        impostos: TAdicionarImposto[];
        codigo_produto_fornecedor?: string;
    }>;
    vencimento_cotacao?: Moment;
};

// Converte string com vírgula para centavos (int)
function formatarMonetarioParaCentavos(valor: string): number {
    const somenteDigitos = valor.replace(/\D/g, ''); // tira tudo que não for dígito
    return Number(somenteDigitos || 0);
}

// Converte centavos (int) para formato "2,49"
function formatarCentavosParaMonetario(valor: number): string {
    const inteiro = Math.floor(valor / 100);
    const centavos = valor % 100;
    return `${inteiro},${centavos.toString().padStart(2, '0')}`;
}